import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { EngravedMeasure } from '@mt/domain';
import { cn } from '../primitives.tsx';
import { loadVexFlow, type NoteMark } from './Staff.tsx';

/**
 * Rhythm notation (VexFlow SVG) on a one-line staff: the time signature, notes
 * and rests with dots, beams by beat, triplet brackets, and ties — drawn from
 * the engraver's output, so what is shown is exactly what the grader expects.
 */
export interface RhythmStaffProps {
  measures: EngravedMeasure[];
  timeSignature: string;
  label: string;
  /** Feedback colour per written item, indexed across all measures in order. */
  marks?: NoteMark[];
  size?: 'md' | 'lg' | 'xl';
  className?: string;
}

const LOGICAL_HEIGHT = 112;
const MARK_COLOR: Record<Exclude<NoteMark, null>, string> = {
  correct: 'var(--color-fern)',
  incorrect: 'var(--color-rose)',
  focus: 'var(--color-primary)',
};

export function RhythmStaff({ measures, timeSignature, label, marks, size = 'lg', className }: RhythmStaffProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect.width ?? 0);
      setWidth((prev) => (Math.abs(prev - w) > 1 ? w : prev));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const measuresKey = JSON.stringify(measures);
  const marksKey = (marks ?? []).join(',');

  useEffect(() => {
    let cancelled = false;
    const el = hostRef.current;
    if (!el || width < 40) return;
    const engraved = JSON.parse(measuresKey) as EngravedMeasure[];
    const markList = marksKey.split(',').map((m) => (m ? (m as NoteMark) : null));
    loadVexFlow()
      .then((VF) => {
        if (cancelled || !hostRef.current) return;
        const target = hostRef.current;
        target.innerHTML = '';
        const [numBeats, beatValue] = timeSignature.split('/').map(Number) as [number, number];
        const perMeasure = engraved.map((m) => Math.max(130, 36 + m.items.length * 30));
        const needed = 50 + perMeasure.reduce((a, b) => a + b, 0);
        const xl = size === 'xl';
        const preferred = xl ? Math.min(3, Math.max(1.2, width / 260)) : size === 'lg' ? Math.min(2, Math.max(1, width / 340)) : Math.min(1.5, Math.max(0.9, width / 400));
        const byHeight = typeof window !== 'undefined' ? (window.innerHeight * (xl ? 0.42 : 0.28)) / LOGICAL_HEIGHT : preferred;
        const scale = Math.max(Math.min(preferred, width / needed, byHeight), 0.7);
        const logicalWidth = width / scale;

        const renderer = new VF.Renderer(target, VF.Renderer.Backends.SVG);
        renderer.resize(width, Math.round(LOGICAL_HEIGHT * scale));
        const ctx = renderer.getContext();
        ctx.scale(scale, scale);
        ctx.setFillStyle('currentColor');
        ctx.setStrokeStyle('currentColor');

        const totalWeight = perMeasure.reduce((a, b) => a + b, 0) + 40;
        const usable = logicalWidth - 12;
        let x = 6;
        let globalIndex = 0;
        const allNotes: InstanceType<VexFlowModuleT['StaveNote']>[] = [];
        engraved.forEach((measure, mi) => {
          const w = (usable * (perMeasure[mi]! + (mi === 0 ? 40 : 0))) / totalWeight;
          const stave = new VF.Stave(x, 0, w, { spaceAboveStaffLn: 4, spaceBelowStaffLn: 2 });
          stave.setConfigForLines([{ visible: false }, { visible: false }, { visible: true }, { visible: false }, { visible: false }]);
          stave.setStyle({ strokeStyle: 'currentColor', fillStyle: 'currentColor' });
          if (mi === 0) stave.addTimeSignature(timeSignature);
          stave.setContext(ctx).draw();
          const notes = measure.items.map((item) => {
            const note = new VF.StaveNote({
              keys: ['b/4'],
              duration: item.code,
              dots: item.dots,
              type: item.rest ? 'r' : undefined,
              stemDirection: VF.Stem.UP,
            });
            if (item.dots) VF.Dot.buildAndAttach([note], { all: true });
            const mark = markList[globalIndex++];
            const color = mark ? MARK_COLOR[mark] : 'currentColor';
            note.setStyle({ fillStyle: color, strokeStyle: color });
            note.setStemStyle({ fillStyle: color, strokeStyle: color });
            note.setFlagStyle({ fillStyle: color, strokeStyle: color });
            return note;
          });
          const beams = measure.beams.map((group) => new VF.Beam(group.map((i) => notes[i]!)));
          // Created before formatting: a tuplet scales its notes' durations to 2⁄3.
          const tuplets = measure.tuplets.map((group) => new VF.Tuplet(group.map((i) => notes[i]!), { numNotes: 3, notesOccupied: 2, ratioed: false }));
          const voice = new VF.Voice({ numBeats, beatValue }).setStrict(false);
          voice.addTickables(notes);
          new VF.Formatter().joinVoices([voice]).format([voice], Math.max(40, stave.getNoteEndX() - stave.getNoteStartX() - 10));
          voice.draw(ctx, stave);
          for (const beam of beams) beam.setContext(ctx).draw();
          for (const tuplet of tuplets) tuplet.setContext(ctx).draw();
          allNotes.push(...notes);
          x += w;
        });
        // Ties, including across the barline.
        const items = engraved.flatMap((m) => m.items);
        items.forEach((item, i) => {
          if (item.tieToNext && allNotes[i + 1]) {
            new VF.StaveTie({ firstNote: allNotes[i], lastNote: allNotes[i + 1], firstIndexes: [0], lastIndexes: [0] }).setContext(ctx).draw();
          }
        });
        const svg = target.querySelector('svg');
        if (svg) {
          svg.setAttribute('aria-hidden', 'true');
          svg.setAttribute('focusable', 'false');
          svg.style.display = 'block';
          svg.style.maxWidth = '100%';
        }
        setReady(true);
        setError(null);
      })
      .catch(() => {
        if (!cancelled) setError('The rhythm could not be drawn. Reload to try again.');
      });
    return () => {
      cancelled = true;
    };
  }, [measuresKey, marksKey, timeSignature, width, size]);

  return (
    <div role="img" aria-label={label} className={cn('relative w-full text-ink', className)}>
      <div ref={hostRef} className={cn('w-full transition-opacity duration-150', ready ? 'opacity-100' : 'opacity-0')} style={{ minHeight: 90 }} />
      {!ready && !error && (
        <div aria-hidden="true" className="absolute inset-0 flex items-center px-3">
          <div className="h-px w-full bg-line-strong" />
        </div>
      )}
      {error && <p className="absolute inset-0 grid place-items-center text-sm text-rose-ink">{error}</p>}
    </div>
  );
}

type VexFlowModuleT = typeof import('vexflow/bravura');
